/**
 * Détail d'un tenant — mode plateforme OneForAll Distribution.
 *
 * Affiche les infos, les stats, et propose 2 actions critiques :
 *   - Démarrer une session hotline (impersonation) → bascule vers /admin
 *     du tenant cible avec bannière rouge persistante.
 *   - Suspendre / réactiver le tenant (avec confirmation pour la suspension).
 *
 * Les modifications de paramètres (nom, ville, etc.) sont aussi disponibles
 * via un formulaire édition basique.
 *
 * Refonte R10 (mai 2026) :
 *   - Lien retour + en-tête avec LogoMark Building2 accent urgent + identité
 *   - Stats grid 4 KPIs en bento glass avec halo urgent radial
 *   - Card hotline glass `border-urgent/40` avec halo urgent + Radio icon en bento
 *   - Card paramètres glass form 2 cols
 *   - Card zone dangereuse glass avec Suspendre/Réactiver workflow
 *
 * Logique métier 100% préservée :
 *   - api.platform.tenants.get/update + useAuth().startImpersonation(id)
 *   - confirmImpersonate 2-step (Alert warning) → handleStartHotline → navigate('/admin')
 *   - confirmSuspend 2-step + handleSuspend → setActif(false)
 *   - handleReactivate → setActif(true) (sans confirmation, action positive)
 *   - notify timer 4s, handleError pour ApiException
 *   - Bouton hotline disabled si tenant inactif (Alert warning au-dessus)
 *   - Suspension bloque connexion TOUS users du tenant
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Button, Input, Field, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  ArrowLeft, Save, Pause, Play, Radio, AlertTriangle, Building2,
  AlertCircle, CheckCircle2, UserPlus,
} from 'lucide-react';
import type { PlatformTenantListItem } from '@roronoa-collectivites/shared-types';

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();

  const [tenant, setTenant] = useState<PlatformTenantListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [confirmImpersonate, setConfirmImpersonate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [nom, setNom] = useState('');
  const [ville, setVille] = useState('');
  const [codePostal, setCodePostal] = useState('');

  // Invitation d'un administrateur (modal)
  const [inviteOpen, setInviteOpen] = useState(false);
  const [iEmail, setIEmail] = useState('');
  const [iPrenom, setIPrenom] = useState('');
  const [iNom, setINom] = useState('');
  const [iLoading, setILoading] = useState(false);
  const [iError, setIError] = useState<string | null>(null);

  const fetchTenant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const t = await api.platform.tenants.get(id);
      setTenant(t);
      setNom(t.nom);
      setVille(t.ville ?? '');
      setCodePostal(t.codePostal ?? '');
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error || 'Tenant introuvable');
      else setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  function notify(msg: string) {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 4000);
  }

  function handleError(err: unknown) {
    if (err instanceof ApiException) setError(err.payload.error || 'Action refusée');
    else setError('Erreur réseau');
    setSuccess(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      const updated = await api.platform.tenants.update(id, {
        nom,
        ville: ville.trim() === '' ? null : ville,
        codePostal: codePostal.trim() === '' ? null : codePostal,
      });
      setTenant(updated);
      notify('Modifications enregistrées.');
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleSuspend() {
    if (!id) return;
    try {
      const updated = await api.platform.tenants.update(id, { actif: false });
      setTenant(updated);
      notify('Collectivité suspendue. Les utilisateurs ne peuvent plus se connecter.');
      setConfirmSuspend(false);
    } catch (err) {
      handleError(err);
    }
  }

  async function handleReactivate() {
    if (!id) return;
    try {
      const updated = await api.platform.tenants.update(id, { actif: true });
      setTenant(updated);
      notify('Collectivité réactivée.');
    } catch (err) {
      handleError(err);
    }
  }

  async function handleStartHotline() {
    if (!id || !tenant) return;
    try {
      await startImpersonation(id);
      // Une fois en mode hotline, on bascule sur le backoffice du tenant cible
      navigate('/admin', { replace: true });
    } catch (err) {
      handleError(err);
    }
  }

  function openInvite() {
    setIEmail('');
    setIPrenom('');
    setINom('');
    setIError(null);
    setInviteOpen(true);
  }

  async function handleInvite() {
    if (!id) return;
    const email = iEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setIError('Saisissez une adresse email valide.');
      return;
    }
    setILoading(true);
    setIError(null);
    try {
      await api.platform.tenants.inviteAdmin(id, {
        email,
        prenom: iPrenom.trim() || undefined,
        nom: iNom.trim() || undefined,
      });
      setInviteOpen(false);
      notify(`Invitation envoyée à ${email}.`);
      // Rafraîchit les stats (le compteur d'admins bougera à l'acceptation)
      fetchTenant();
    } catch (err) {
      if (err instanceof ApiException) {
        setIError(err.payload.error || 'Invitation impossible');
      } else {
        setIError('Erreur réseau');
      }
    } finally {
      setILoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-urgent border-t-transparent"
          role="status"
          aria-label="Chargement"
        />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="animate-fade-in space-y-4">
        <Alert variant="error" icon={AlertCircle}>
          {error ?? 'Collectivité introuvable.'}
        </Alert>
        <Link
          to="/platform/collectivites"
          className="inline-flex items-center gap-1 rounded-r-md px-1 text-sm font-medium text-urgent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour
        </Link>
      </div>
    );
  }

  return (
    <section aria-labelledby="tenant-title" className="animate-fade-in">
      <Link
        to="/platform/collectivites"
        className="mb-4 inline-flex items-center gap-1 rounded-r-md px-1 text-sm font-medium text-ink-muted transition-colors hover:text-urgent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Toutes les collectivités
      </Link>

      <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <LogoMark icon={Building2} accent="urgent" size="lg" />
          <div className="min-w-0">
            <SectionEyebrow as="div">Plateforme · collectivité</SectionEyebrow>
            <h1
              id="tenant-title"
              className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl truncate"
            >
              {tenant.nom}
            </h1>
            <p className="mt-0.5 font-mono text-xs text-ink-muted truncate">
              {tenant.slug}
              {tenant.codeInsee ? ` · INSEE ${tenant.codeInsee}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {tenant.actif ? (
            <Badge variant="success">Actif</Badge>
          ) : (
            <Badge variant="warning">Suspendu</Badge>
          )}
          {!tenant.visiblePublique && <Badge variant="neutral">Privé</Badge>}
        </div>
      </header>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" icon={CheckCircle2} className="mb-4">
          {success}
        </Alert>
      )}

      {/* Stats — bento KPI glass */}
      <Card variant="glass" padded className="mb-4">
        <SectionEyebrow as="h2" className="mb-3">Statistiques</SectionEyebrow>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <PlatformKpi label="Utilisateurs total" value={String(tenant.stats.usersTotal)} />
          <PlatformKpi label="Actifs" value={String(tenant.stats.usersActifs)} />
          <PlatformKpi label="Admins" value={String(tenant.stats.adminCount)} />
          <PlatformKpi
            label="Dernière activité"
            value={
              tenant.stats.lastActivityAt
                ? new Date(tenant.stats.lastActivityAt).toLocaleDateString('fr-FR')
                : '—'
            }
            small
          />
        </dl>
      </Card>

      {/* Administrateurs — invitation directe depuis la plateforme (hors hotline) */}
      <Card variant="glass" padded className="mb-4">
        <div className="flex items-start gap-3">
          <div
            className="inline-grid h-10 w-10 place-items-center rounded-r-lg bg-tech/15 text-tech-fg flex-shrink-0"
            aria-hidden="true"
          >
            <UserPlus className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <SectionEyebrow as="h2">Administrateurs</SectionEyebrow>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
              Invitez le maire et les administrateurs de{' '}
              <strong className="text-ink">{tenant.nom}</strong>. Chacun reçoit un
              email pour créer son compte et définir son mot de passe. Vous pouvez
              en inviter autant que nécessaire — un administrateur pourra ensuite
              inviter les autres comptes (managers, agents) depuis son backoffice.
            </p>
            <Button variant="primary" size="md" onClick={openInvite} className="mt-3">
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Inviter un administrateur
            </Button>
          </div>
        </div>
      </Card>

      {/* Hotline — Card glass border urgent + halo */}
      <Card
        variant="glass"
        padded
        className="mb-4 border-urgent/40"
      >
        <div className="flex items-start gap-3">
          <div
            className="inline-grid h-10 w-10 place-items-center rounded-r-lg bg-urgent/15 text-urgent flex-shrink-0"
            aria-hidden="true"
          >
            <Radio className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <SectionEyebrow as="h2" className="text-urgent">Mode hotline</SectionEyebrow>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
              Démarre une session de support à distance sur <strong className="text-ink">{tenant.nom}</strong>. Tu agiras
              comme un super_admin sur ce tenant pendant 15 minutes. L'admin du tenant cible
              recevra un email de notification (transparence RGPD art. 28). Toutes tes actions seront
              tracées séparément dans le journal plateforme.
            </p>

            {!tenant.actif && (
              <Alert variant="warning" icon={AlertTriangle} className="mt-3">
                Cette collectivité est suspendue. Réactive-la d'abord pour démarrer une session
                hotline.
              </Alert>
            )}

            {confirmImpersonate ? (
              <Alert variant="warning" icon={AlertTriangle} className="mt-3">
                <div>
                  <p className="font-medium">Démarrer la session hotline ?</p>
                  <p className="mt-1 text-sm">
                    L'admin de <strong>{tenant.nom}</strong> recevra un email immédiatement.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button variant="danger" size="sm" onClick={handleStartHotline}>
                      <Radio className="h-4 w-4" aria-hidden="true" />
                      Oui, démarrer la session
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setConfirmImpersonate(false)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : (
              <Button
                variant="danger"
                size="md"
                onClick={() => setConfirmImpersonate(true)}
                disabled={!tenant.actif}
                className="mt-3"
              >
                <Radio className="h-4 w-4" aria-hidden="true" />
                Démarrer une session hotline
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Édition paramètres */}
      <Card variant="glass" padded className="mb-4">
        <SectionEyebrow as="h2" className="mb-3">Paramètres</SectionEyebrow>
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Nom" htmlFor="t-nom" required>
              <Input id="t-nom" value={nom} onChange={(e) => setNom(e.target.value)} />
            </Field>
            <Field label="Ville" htmlFor="t-ville">
              <Input id="t-ville" value={ville} onChange={(e) => setVille(e.target.value)} />
            </Field>
            <Field label="Code postal" htmlFor="t-cp">
              <Input
                id="t-cp"
                value={codePostal}
                onChange={(e) => setCodePostal(e.target.value)}
              />
            </Field>
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit" variant="primary" disabled={saving}>
              <Save className="h-4 w-4" aria-hidden="true" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </Card>

      {/* Zone dangereuse */}
      <Card variant="glass" padded>
        <SectionEyebrow as="h2" className="mb-2 text-urgent">Zone dangereuse</SectionEyebrow>
        {tenant.actif ? (
          <>
            <p className="text-sm text-ink-muted leading-relaxed">
              Suspendre cette collectivité bloque la connexion de <strong className="text-ink">TOUS</strong> ses utilisateurs (admin,
              manager, agent, administrés). À utiliser pour les arrêts de service planifiés ou
              en cas d'urgence sécurité.
            </p>
            {confirmSuspend ? (
              <Alert variant="warning" icon={AlertTriangle} className="mt-3">
                <div>
                  <p className="font-medium">Suspendre {tenant.nom} ?</p>
                  <p className="mt-1 text-sm">
                    Action immédiate. Réversible via "Réactiver".
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button variant="danger" size="sm" onClick={handleSuspend}>
                      Oui, suspendre
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setConfirmSuspend(false)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setConfirmSuspend(true)} className="mt-3">
                <Pause className="h-4 w-4" aria-hidden="true" /> Suspendre la collectivité
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-ink-muted leading-relaxed">
              Cette collectivité est suspendue. Réactive-la pour permettre la connexion des
              utilisateurs.
            </p>
            <Button variant="success" size="sm" onClick={handleReactivate} className="mt-3">
              <Play className="h-4 w-4" aria-hidden="true" /> Réactiver la collectivité
            </Button>
          </>
        )}
      </Card>

      {/* Modal — invitation d'un administrateur */}
      <Dialog
        open={inviteOpen}
        onClose={iLoading ? () => {} : () => setInviteOpen(false)}
        title="Inviter un administrateur"
        description={`Un email d'invitation sera envoyé pour créer un compte administrateur de ${tenant.nom}.`}
        variant="info"
        hideCloseButton={iLoading}
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setInviteOpen(false)}
              disabled={iLoading}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleInvite}
              loading={iLoading}
              disabled={iLoading || iEmail.trim() === ''}
            >
              Envoyer l'invitation
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleInvite();
          }}
          className="space-y-4"
        >
          {iError && (
            <Alert variant="error" icon={AlertCircle}>
              {iError}
            </Alert>
          )}
          <Field label="Email de l'administrateur" htmlFor="invite-email" required>
            <Input
              id="invite-email"
              type="email"
              value={iEmail}
              onChange={(e) => setIEmail(e.target.value)}
              placeholder="maire@exemple.fr"
              maxLength={255}
              autoFocus
            />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Prénom (optionnel)" htmlFor="invite-prenom">
              <Input
                id="invite-prenom"
                value={iPrenom}
                onChange={(e) => setIPrenom(e.target.value)}
                maxLength={100}
              />
            </Field>
            <Field label="Nom (optionnel)" htmlFor="invite-nom">
              <Input
                id="invite-nom"
                value={iNom}
                onChange={(e) => setINom(e.target.value)}
                maxLength={100}
              />
            </Field>
          </div>
          <p className="text-xs text-ink-subtle">
            L'administrateur reçoit un lien valable plusieurs jours. C'est lui qui
            choisit son mot de passe — vous n'avez pas à le créer ni à le connaître.
          </p>
        </form>
      </Dialog>
    </section>
  );
}

// ============================================================================
// Composant KPI interne (accent urgent par défaut, halo radial subtle)
// ============================================================================

function PlatformKpi({
  label, value, small,
}: {
  label: string;
  value: string;
  small?: boolean;
}) {
  return (
    <div
      className="rounded-r-xl border border-border bg-surface/55 backdrop-blur-glass p-3 shadow-soft transition-all hover:shadow-glass-hi"
      style={{
        backgroundImage:
          'radial-gradient(120px 60px at 0% 0%, oklch(var(--c-urgent) / 0.10), transparent 70%), ' +
          'linear-gradient(180deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))',
      }}
    >
      <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-muted">{label}</dt>
      <dd
        className={
          small
            ? 'mt-1 text-sm text-ink tabular-nums'
            : 'mt-1 font-display text-2xl font-semibold tracking-r-tight tabular-nums text-ink'
        }
      >
        {value}
      </dd>
    </div>
  );
}
